import { Link, Outlet, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Logo } from "@/components/ui/logo";
import { useDocSections } from "@/hooks/useDocSections";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { cn } from "@/lib/utils";
import { docSections as staticSections } from "@/content/docs";

export default function DocsLayout() {
  const { section } = useParams<{ section?: string }>();
  const activeSlug = section || "getting-started";
  const { data: sections } = useDocSections();
  const { isAdmin } = useAdminAuth();

  // Use fetched sections or fall back to static
  const navSections = sections || staticSections;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Documentation"
        description="Complete documentation for Reqqy — learn how to review requirements, use personas, understand clarity scores, and integrate via API."
        canonical="/docs"
      />

      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-40">
        <div className="container flex items-center justify-between h-16 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo variant="full" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button asChild variant="outline" size="sm">
                <Link to="/admin/docs">
                  <Pencil className="mr-2 h-4 w-4" />
                  Manage Docs
                </Link>
              </Button>
            )}
            <Button asChild variant="ghost" size="sm">
              <Link to="/home">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <div className="container px-4 md:px-6 py-10 md:py-14">
        <div className="flex gap-10">
          <aside className="hidden lg:block w-60 shrink-0">
            <nav className="sticky top-24">
              <h2 className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">
                Documentation
              </h2>
              <ul className="space-y-0.5">
                {navSections.map((s) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.slug}>
                      <Link
                        to={`/docs/${s.slug}`}
                        className={cn(
                          "flex items-center gap-2.5 text-sm py-2 px-3 rounded-md transition-colors",
                          activeSlug === s.slug
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        {s.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-8 pt-6 border-t border-border">
                <Button asChild variant="outline" size="sm" className="w-full">
                  <a href="mailto:support@reqqy.app">Contact Support</a>
                </Button>
              </div>
            </nav>
          </aside>

          <main className="flex-1 min-w-0 max-w-3xl">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
